import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageService } from '../../../../services/message-service/message.service';
import {
    EinstallenMeldenDialogInput,
    EinstallenMeldenDialogResult,
} from '../../classes/einstallen-melden';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
    EinstallenHitOptionen,
    EinstallenMeldenOverview,
    EinstallenSubmitFlowService,
} from '../einstallen-submit-flow.service';

@Component({
    selector: 'kiene-einstallen-melden-dialog',
    templateUrl: './einstallen-melden-dialog.component.html',
    styleUrls: ['./einstallen-melden-dialog.component.scss'],
})
export class EinstallenMeldenDialogComponent implements OnInit {
    subscription: Subscription;
    isLoading = true;
    submitLocked = true;

    pass_und_ohrmarke = false;
    nur_pass_gescannt = false;
    nur_ohrmarke_gescannt = false;
    weder_pass_noch_ohrmarke = false;

    anzahl_pass_ohrmarke = 0;
    anzahl_pass = 0;
    anzahl_ohrmarke = 0;
    anzahl_csv = 0;
    nicht_meldbar = 0;

    constructor(
        public dialogRef: MatDialogRef<EinstallenMeldenDialogComponent>,
        private messageService: MessageService,
        private readonly einstallenSubmitFlow: EinstallenSubmitFlowService,
        @Inject(MAT_DIALOG_DATA) public data: EinstallenMeldenDialogInput,
    ) {
        this.ladeTiere();
    }

    ladeTiere() {
        this.isLoading = true;
        this.anzahl_pass_ohrmarke = 0;
        this.anzahl_pass = 0;
        this.anzahl_ohrmarke = 0;
        this.anzahl_csv = 0;
        this.nicht_meldbar = 0;
        this.subscription?.unsubscribe();

        const meldenScope = this.getMeldenScope();
        const load$ = meldenScope.useTierlisteScope
            ? this.einstallenSubmitFlow
                  .ensureFullTierlisteForReview(this.data?.gruppe_id, meldenScope.einstallTierlisteId)
                  .pipe(
                      switchMap(() =>
                          this.einstallenSubmitFlow.loadMeldenOverview(this.data?.gruppe_id, meldenScope.overviewOptions),
                      ),
                  )
            : this.einstallenSubmitFlow.loadMeldenOverview(this.data?.gruppe_id, meldenScope.overviewOptions);

        this.subscription = load$.subscribe({
            next: (overview: EinstallenMeldenOverview) => {
                this.anzahl_pass_ohrmarke = overview.anzahlPassUndOhrmarke;
                this.anzahl_pass = overview.anzahlPass;
                this.anzahl_ohrmarke = overview.anzahlOhrmarke;
                this.anzahl_csv = overview.anzahlCsv;
                this.nicht_meldbar = overview.nichtMeldbar;
                const optionen = this.einstallenSubmitFlow.deriveHitOptionenFromOverview(overview);
                this.pass_und_ohrmarke = optionen.passUndOhrmarke;
                this.nur_pass_gescannt = optionen.nurPassGescannt;
                this.nur_ohrmarke_gescannt = optionen.nurOhrmarkeGescannt;
                this.weder_pass_noch_ohrmarke = optionen.wederPassNochOhrmarke;
                this.isLoading = false;
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden!', err);
                this.isLoading = false;
            },
        });
    }

    ngOnInit(): void {
        const lockMs = this.data?.submitLockMs ?? 350;
        setTimeout(() => {
            this.submitLocked = false;
        }, lockMs);
    }

    variablenSpeichern() {
        this.einstallenSubmitFlow.saveHitOptionen(this.getHitOptionen());
    }

    save() {
        if (this.submitLocked) {
            return;
        }
        this.variablenSpeichern();
        const meldenScope = this.getMeldenScope();
        const submitInput = this.einstallenSubmitFlow.buildMeldenSubmitInput(this.getHitOptionen(), {
            gruppe_id: this.data?.gruppe_id,
            gs_id: this.data?.gs_id,
        }, { wholeGroup: meldenScope.wholeGroup });
        this.einstallenSubmitFlow
            .submitHitMelden(submitInput)
            .then(saved => {
                if (!saved) {
                    return;
                }

                const result = new EinstallenMeldenDialogResult();
                result.ok = true;
                result.melden = true;
                this.dialogRef.close(result);
            })
            .catch(err => {
                this.messageService.errorMessage('Fehler beim Melden der Tiere!', err);
            });
    }

    close() {
        const result = new EinstallenMeldenDialogResult();
        result.ok = false;
        this.dialogRef.close(result);
    }

    tiereOhneEinstalldatumAnzeigen() {
        const result = new EinstallenMeldenDialogResult();
        result.tiereOhneEinstalldatumAnzeigen = true;
        this.dialogRef.close(result);
    }

    get canSubmitMelden(): boolean {
        return (
            (this.pass_und_ohrmarke && this.anzahl_pass_ohrmarke > 0) ||
            (this.nur_pass_gescannt && this.anzahl_pass > 0) ||
            (this.nur_ohrmarke_gescannt && this.anzahl_ohrmarke > 0) ||
            (this.weder_pass_noch_ohrmarke && this.anzahl_csv > 0)
        );
    }

    private getHitOptionen(): EinstallenHitOptionen {
        return {
            passUndOhrmarke: this.pass_und_ohrmarke,
            nurPassGescannt: this.nur_pass_gescannt,
            nurOhrmarkeGescannt: this.nur_ohrmarke_gescannt,
            wederPassNochOhrmarke: this.weder_pass_noch_ohrmarke,
        };
    }

    private getMeldenScope(): {
        wholeGroup: boolean;
        einstallTierlisteId: number | null;
        scopeToSessionScans: boolean;
        useTierlisteScope: boolean;
        overviewOptions: { wholeGroup?: boolean; einstallTierlisteId?: number; scopeToSessionScans?: boolean };
    } {
        if (this.data?.wholeGroup) {
            return {
                wholeGroup: true,
                einstallTierlisteId: null,
                scopeToSessionScans: false,
                useTierlisteScope: false,
                overviewOptions: { wholeGroup: true },
            };
        }

        const einstallTierlisteId = this.einstallenSubmitFlow.getCurrentEinstallTierlisteId();
        const overviewOptions = this.einstallenSubmitFlow.getEinstallMeldenOverviewOptions(einstallTierlisteId);
        const tierlisteId = Number(einstallTierlisteId || 0);

        return {
            wholeGroup: false,
            einstallTierlisteId: tierlisteId > 0 ? tierlisteId : null,
            scopeToSessionScans: !!overviewOptions.scopeToSessionScans,
            useTierlisteScope: tierlisteId > 0,
            overviewOptions,
        };
    }
}
